import type { StackFrame } from "@sentry/browser";

import type { StackFrameClassifier } from "./types";

const DEFAULT_SPAM_STACK_FRAGMENTS: (string | RegExp)[] = [
	"https://script.hotjar.com",
	"https://bat.bing.com/bat.js",
	"chrome-extension://haldlgldplgnggkjaafhelgiaglafanh",
];

const DEFAULT_SPAM_MESSAGES: (string | RegExp)[] = [
	"ResizeObserver loop limit exceeded",
	"ResizeObserver loop completed with undelivered notifications.",
	// Requests canceled by the app or by navigation
	/^canceled$/i,
	"The user aborted a request",
	"signal is aborted",
];

const DEFAULT_IGNORE_MESSAGES: (string | RegExp)[] = [
	"Script error",
	"ResizeObserver loop limit exceeded",
	"ResizeObserver loop completed with undelivered notifications.",
	"Tracking error",
	"Network Error",
	"Request timeout",
];

const matches = (value: string, patterns: (string | RegExp)[]): boolean => {
	if (!value) return false;
	return patterns.some(pattern =>
		typeof pattern === "string" ? value.indexOf(pattern) > -1 : pattern.test(value)
	);
};

export const isSpamError = (
	message: string,
	stackText: string,
	extraStackFragments: (string | RegExp)[] = [],
	extraMessages: (string | RegExp)[] = []
): boolean =>
	matches(message, [...DEFAULT_SPAM_MESSAGES, ...extraMessages]) ||
	matches(stackText, [...DEFAULT_SPAM_STACK_FRAGMENTS, ...extraStackFragments]);

export const isIgnoredMessage = (message: string, extra: (string | RegExp)[] = []): boolean =>
	matches(message, [...DEFAULT_IGNORE_MESSAGES, ...extra]);

export const isExternalError = (
	frames: StackFrame[] | undefined,
	isInternalFrame: StackFrameClassifier | undefined,
	isVendorFrame: StackFrameClassifier | undefined
): boolean => {
	if (!frames?.length || !isInternalFrame) return false;
	if (isVendorFrame && frames.some(isVendorFrame)) return false;
	return !frames.some(isInternalFrame);
};

export const stackFramesToText = (frames: StackFrame[] | undefined): string => {
	if (!frames?.length) return "";
	return frames
		.map(frame => `${frame.function ?? "?"}@${frame.filename ?? ""}:${frame.lineno ?? ""}`)
		.join("\n");
};
